// src/encounter/intelligence/explanation.ts
//
// The Explain card's payload, `scrips.intelligence.explanation/1` (ExplanationV1), as the engine sends it from
// POST /api/intelligence/explain (scrips-clinician-demo `src/engine/explain.ts` at c1728c4, AC-42 stamped). This module
// is pure: it checks a payload when it arrives (encounter owner ruling A2: versioned, bounded, the app owns every label)
// and reads it into what the card draws. It writes no clinical sentence. Every clinical string on the card is either a
// field of the payload, verbatim, or one of the fixed lines below, each quoted from a ruling:
//   - encounter-clinical-informatics-authority.md, EMR terminology T1-T11, D-5275, D-5276 (R1, X5), D-5278 (O1, O2);
//   - the round 18e design brief, section C (Read and Limits wording);
//   - the product bible v6 state copy table (busy, technical, timeout, access-denied).
// Nothing here logs. Record text never leaves this module except into the card.

// ── The contract (a client mirror of the engine's types) ──────────────────────────────────────────────────────
export const EXPLAIN_SCHEMA = 'scrips.intelligence.explanation/1' as const;

export type SourceKind = 'lab' | 'study' | 'problem' | 'medication' | 'state' | 'vital' | 'device-reading' | 'score' | 'document' | 'allergy';
export type FlagClass = 'critical' | 'not-classified' | 'abnormal' | 'qualitative' | 'normal-code' | 'blank' | 'unavailable';
export type ResultStatus = 'final' | 'preliminary' | 'corrected' | 'amended' | 'unknown';
export type DateBasis = 'specimen' | 'issued' | 'unknown' | 'legacy';
export type Verdict = 'passed' | 'failed' | 'not-run' | 'not-applicable';
/** O2: the plain check state the engine writes (22a4b4d); rendered verbatim when sent. */
export type CheckState = 'passed' | 'did not pass' | 'did not run' | 'not applicable';
export type ExplainState = 'answered' | 'withheld' | 'denied' | 'failed';
export type Reason =
  | 'not-enabled'
  | 'access-denied'
  | 'consent-withdrawn'
  | 'check-failed'
  | 'check-missing'
  | 'unsupported-sentence'
  | 'timeout'
  | 'busy'
  | 'duplicate-request'
  | 'technical';

export interface Source {
  kind: SourceKind;
  name: string;
  value?: string;
  unit?: string;
  comparator?: string;
  printedFlag?: string;
  lab?: string;
  method?: string;
  measuredOn?: string;
  reportedOn?: string;
  printedInterval?: string;
  note?: string;
  code?: string;
  clinicalStatus?: string;
  verificationStatus?: string;
  conflictingStatus?: boolean;
  flagClass?: FlagClass;
  flagCode?: string;
  preliminary?: true;
  status?: ResultStatus;
  dateBasis?: DateBasis;
  recordId: string;
}
export interface Line {
  text: string;
  cites: string[];
  priority: 'summary' | 'detail';
}
export type GapType = 'absent' | 'unavailable' | 'old' | 'differences';
export type Block =
  | { kind: 'headline'; text: string; cites: string[] }
  | { kind: 'fact_rows'; rows: Line[] }
  | { kind: 'gaps'; items: Array<Line & { type: GapType }> }
  | { kind: 'limits'; items: string[] };
export interface SeriesPoint {
  recordId: string;
  date: string;
  value: string;
  unit?: string;
  printedInterval?: string;
  lab?: string;
  flagClass: FlagClass;
  dateBasis: DateBasis;
  preliminary?: true;
  status?: ResultStatus;
}
export interface SeriesV1 {
  id: string;
  test: string;
  analyte: string;
  kind: 'lab' | 'study';
  lab: string;
  unit: string;
  /** "" when the reference range changed over the series; each point then keeps its own printedInterval. */
  printedInterval: string;
  /** Engine c52fe7c: sent with an empty printedInterval ("Reference range changed over time; see each result."). */
  rangeNote?: string;
  code?: string;
  method: string;
  trendable: boolean;
  points: SeriesPoint[];
  omitted: number;
}
export interface Binding {
  orgId: string;
  patientId: string;
  encounterId: string | null;
  snapshot: string;
  asOf: string;
}
export interface Check {
  id: string;
  name: string;
  slot: string;
  verdict: Verdict;
  state?: CheckState;
}
export interface ExplanationV1 {
  schema: typeof EXPLAIN_SCHEMA;
  requestId: string;
  system: string;
  binding: Binding;
  state: ExplainState;
  reason?: Reason;
  retryable: boolean;
  answeredAt: string;
  agent: { key: string; name: string };
  checkSet: string;
  checks: Check[];
  title?: string;
  blocks?: Block[];
  sources?: Record<string, Source>;
  critical?: { rows: Line[]; sources: Record<string, Source> };
  series?: SeriesV1[];
  resultsNotInSeries?: number;
  label: 'AI draft';
  /** T-08: on a withheld card, and on a card whose check could not run (engine `withheldLineOf`). Rendered verbatim;
   *  the card writes no withheld text of its own (#825 rule 2a). */
  withheldLine?: string;
  /** O1: the card's origin; `label` is rendered verbatim, `kind` drives logic (#825 rule 2b). */
  origin?: { kind: OriginKind; label: string };
  /** R1: the ruled Read lines, rendered verbatim instead of any UI-typed Read text. */
  readLines?: string[];
  /** O2: the fixed line under the checks, rendered verbatim. */
  checksNote?: string;
  /** O2: what the card was made from (inputs, rule set and version, AI check deployment, output id). */
  produced?: Produced;
  /** The approved full limits list behind the at-rest line (fixed text from the engine). */
  limitsFull?: { read: string[]; shownNotUsed: string[]; notRead: string[] };
  /** D-5275: header and "Recorded at visits" data, display only (never sent to the AI check). */
  display?: DisplayV1;
}

export type OriginKind = 'from-record' | 'scrips-rule' | 'ai-draft';
export interface Produced {
  inputs: Array<{ recordId: string; name: string; value?: string; unit?: string; date?: string }>;
  rules: { name: string; version: string };
  model: { deployment: string; role: string } | null;
  outputId: string;
}
/** D-5275 display block (engine explainDisplay.ts at 22a4b4d). Every string is the engine's, rendered verbatim. */
export interface VitalReading {
  kind: 'heart-rate' | 'weight';
  sourceId: string;
  captureId: string;
  date: string | null;
  time: string | null;
  text: string;
  value: string;
  unit: string;
  status: ResultStatus;
  tag: string;
  measurementSource: string;
  plottable: boolean;
}
export type VitalsDisplay =
  | { label: string; status: 'read'; window: string; readings: VitalReading[]; notShown: Array<{ sourceId: string; reason: string }>; notYetShown?: string }
  | { label: string; status: 'unavailable'; reason: string };
export interface DisplayV1 {
  readAt: { date: string; time: string };
  age: { years: number } | { text: string };
  sex: { recorded: string } | { text: string };
  vitals: VitalsDisplay;
}

/** O1 fallback for a payload that predates the `origin` field: its only label is "AI draft". */
const AI_DRAFT_FALLBACK = 'AI draft · review before use';
/** The origin words: the payload's `origin.label`, verbatim; the AI-draft words only when no origin is sent. */
export const originOf = (x: Pick<ExplanationV1, 'origin' | 'label'>): string => x.origin?.label ?? AI_DRAFT_FALLBACK;

/** T11: final has no tag. A tag states only the carried status; it never claims a value changed. */
export const STATUS_TAG: Record<ResultStatus, string> = { final: '', preliminary: 'Prelim', corrected: 'Corrected', amended: 'Amended', unknown: 'Status unknown' };
/** T11: the accessible name of the Prelim tag. */
export const STATUS_TAG_ARIA: Record<ResultStatus, string> = { final: '', preliminary: 'Preliminary result', corrected: 'Corrected', amended: 'Amended', unknown: 'Status unknown' };

// ── Fixed lines (each quoted from its ruling; never composed) ────────────────────────────────────────────────
export const WORDS = {
  /** T6 / X5. */
  criticalTag: 'Critical · Scrips does not record acknowledgement',
  /** T7. */
  outsideMarker: 'Outside ref range · compared by Scrips',
  /** T3. */
  seeReport: 'See report',
  rangeNote: 'Printed by the laboratory; not compared by Scrips. This card does not set goals for this patient.',
  /** T1, T3, T5. */
  flagHeader: 'Lab flag',
  rangeHeader: 'Ref range',
  noFlagAria: 'no laboratory flag',
  flagUnavailable: 'Unavailable',
  /** T8. */
  mostRecent: 'Most recent on file',
  labNotStated: 'laboratory not stated',
  /** O2: the link to the checks (a control label; the engine sends the checks and their note). */
  howProduced: 'How this was produced',
  /** Round 18e C: the footer everywhere. */
  footer: 'Nothing is added to the chart until you act.',
} as const;

/** O2: the four check states, in words. "not applicable" is never sent by the engine today. */
export const VERDICT_WORD: Record<Verdict, string> = { passed: 'passed', failed: 'did not pass', 'not-run': 'did not run', 'not-applicable': 'not applicable' };

/** The visible line for a state that is not an answer. `null` = no card at all (not-enabled, R4-2). The withheld and
 *  failed lines are the bible v6 copy table as amended by T-08 (the check name moves to "How this was produced"). */
export const STATE_LINE: Record<Reason, string | null> = {
  'not-enabled': null,
  'access-denied': 'Not available for your role.',
  'consent-withdrawn': 'Consent for Scrips Intelligence is no longer on file. This explanation has been cleared.',
  // Withheld: the engine's own T-08 string, verbatim (`withheldLine`); the card writes none (#825 rule 2a).
  'check-failed': null,
  'check-missing': null,
  'unsupported-sentence': null,
  busy: 'Another explanation is running. Try again in a moment.',
  technical: 'Scrips Intelligence is not available. The visit continues without it.',
  timeout: 'Scrips Intelligence did not answer in time. The visit continues without it.',
  // "No new copy. The first request's card stays." The hook keeps the earlier card; this is never drawn.
  'duplicate-request': null,
};

/** Session words reused from the engine client (intelligence-engine.ts), so one refusal reads the same everywhere. */
export const SESSION_EXPIRED = 'Your session has expired. Sign in again to use Scrips Intelligence.';

// ── Payload check (A2) ───────────────────────────────────────────────────────────────────────────────────────
// The engine's own bounds (explain.ts BOUNDS) plus a margin for counted lines; anything larger is not drawn.
// rows and sources: the engine grows its block for pinned critical rows (never cut), so the cap is generous.
const MAX = { text: 400, title: 80, rows: 500, blocks: 8, sources: 500, series: 40, points: 60, id: 160, field: 400, checks: 8 };
const STATES: readonly ExplainState[] = ['answered', 'withheld', 'denied', 'failed'];
const REASONS: readonly Reason[] = ['not-enabled', 'access-denied', 'consent-withdrawn', 'check-failed', 'check-missing', 'unsupported-sentence', 'timeout', 'busy', 'duplicate-request', 'technical'];
const VERDICTS: readonly Verdict[] = ['passed', 'failed', 'not-run', 'not-applicable'];
const FLAG_CLASSES: readonly FlagClass[] = ['critical', 'not-classified', 'abnormal', 'qualitative', 'normal-code', 'blank', 'unavailable'];
const STATUSES: readonly ResultStatus[] = ['final', 'preliminary', 'corrected', 'amended', 'unknown'];

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown, max = MAX.field): v is string => typeof v === 'string' && v.length <= max;
const optStr = (v: unknown, max = MAX.field) => v === undefined || str(v, max);
const strArr = (v: unknown, maxItems: number, max = MAX.id): v is string[] => Array.isArray(v) && v.length <= maxItems && v.every((x) => str(x, max));

function okLine(v: unknown): boolean {
  return isObj(v) && str(v.text, MAX.text) && strArr(v.cites, MAX.sources) && (v.priority === 'summary' || v.priority === 'detail');
}
function okSource(v: unknown): boolean {
  if (!isObj(v) || !str(v.name) || !str(v.recordId, MAX.id) || typeof v.kind !== 'string') return false;
  for (const k of ['value', 'unit', 'comparator', 'printedFlag', 'lab', 'method', 'measuredOn', 'reportedOn', 'printedInterval', 'note', 'code', 'clinicalStatus', 'verificationStatus', 'flagCode', 'dateBasis'] as const) {
    if (!optStr(v[k])) return false;
  }
  if (v.flagClass !== undefined && !FLAG_CLASSES.includes(v.flagClass as FlagClass)) return false;
  if (v.status !== undefined && !STATUSES.includes(v.status as ResultStatus)) return false;
  return true;
}
function okSources(v: unknown): boolean {
  return isObj(v) && Object.keys(v).length <= MAX.sources && Object.entries(v).every(([k, s]) => str(k, MAX.id) && okSource(s));
}
function okBlock(v: unknown): boolean {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case 'headline':
      return str(v.text, MAX.text) && strArr(v.cites, MAX.sources);
    case 'fact_rows':
      return Array.isArray(v.rows) && v.rows.length <= MAX.rows && v.rows.every(okLine);
    case 'gaps':
      return Array.isArray(v.items) && v.items.length <= MAX.rows && v.items.every((i) => okLine(i) && typeof (i as Obj).type === 'string');
    case 'limits':
      return strArr(v.items, MAX.rows, MAX.text);
    default:
      return false;
  }
}
function okProduced(v: unknown): boolean {
  if (!isObj(v) || !str(v.outputId, MAX.id) || !isObj(v.rules) || !str(v.rules.name, 120) || !str(v.rules.version, 60)) return false;
  if (v.model !== null && !(isObj(v.model) && str(v.model.deployment, 80) && str(v.model.role, 40))) return false;
  return Array.isArray(v.inputs) && v.inputs.length <= MAX.sources && v.inputs.every((i) => isObj(i) && str(i.recordId, MAX.id) && str(i.name) && optStr(i.value, 40) && optStr(i.unit, 40) && optStr(i.date, 40));
}
function okDisplay(v: unknown): boolean {
  if (!isObj(v) || !isObj(v.readAt) || !str(v.readAt.date, 20) || !str(v.readAt.time, 10)) return false;
  // Each union is checked on the branch the renderer selects ('years' in age, 'recorded' in sex), not on "any branch
  // valid": { recorded: {…}, text: 'Unknown' } passed the loose check and the header then drew an object (React throws).
  if (!isObj(v.age) || !('years' in v.age ? typeof v.age.years === 'number' && Number.isFinite(v.age.years) : str(v.age.text, 80))) return false;
  if (!isObj(v.sex) || !('recorded' in v.sex ? str(v.sex.recorded, 40) : str(v.sex.text, 80))) return false;
  const vi = v.vitals;
  if (!isObj(vi) || !str(vi.label, 60)) return false;
  if (vi.status === 'unavailable') return str(vi.reason, MAX.text);
  if (vi.status !== 'read' || !str(vi.window, MAX.text) || !optStr(vi.notYetShown, MAX.text) || !Array.isArray(vi.notShown) || vi.notShown.length > 40) return false;
  return (
    Array.isArray(vi.readings) &&
    vi.readings.length <= 40 &&
    vi.readings.every((r) => isObj(r) && str(r.text, 40) && str(r.sourceId, MAX.id) && str(r.tag, 20) && str(r.measurementSource, 80) && (r.date === null || str(r.date, 20)) && (r.time === null || str(r.time, 10)))
  );
}
function okSeries(v: unknown): boolean {
  if (!isObj(v) || !str(v.id, MAX.id) || !str(v.test) || !str(v.lab) || !str(v.unit) || !str(v.printedInterval) || !optStr(v.rangeNote, MAX.text) || typeof v.trendable !== 'boolean') return false;
  return (
    Array.isArray(v.points) &&
    v.points.length <= MAX.points &&
    v.points.every((p) => isObj(p) && str(p.recordId, MAX.id) && str(p.date, 40) && str(p.value, 40) && FLAG_CLASSES.includes(p.flagClass as FlagClass))
  );
}

/**
 * The payload, when it is a well-formed ExplanationV1 within bounds; otherwise null (the card then shows the technical
 * line, never the body). Unknown extra fields are ignored, never drawn.
 */
export function parseExplanation(raw: unknown): ExplanationV1 | null {
  if (!isObj(raw) || raw.schema !== EXPLAIN_SCHEMA) return null;
  if (!str(raw.requestId, 64) || !str(raw.system, 64) || !STATES.includes(raw.state as ExplainState)) return null;
  if (raw.reason !== undefined && !REASONS.includes(raw.reason as Reason)) return null;
  if (!isObj(raw.binding) || !str(raw.binding.patientId, MAX.id)) return null;
  if (!Array.isArray(raw.checks) || raw.checks.length > MAX.checks) return null;
  if (!raw.checks.every((c) => isObj(c) && str(c.id, 40) && str(c.name, 80) && VERDICTS.includes(c.verdict as Verdict))) return null;
  if (raw.title !== undefined && !str(raw.title, MAX.title)) return null;
  if (raw.origin !== undefined && !(isObj(raw.origin) && (['from-record', 'scrips-rule', 'ai-draft'] as unknown[]).includes(raw.origin.kind) && str(raw.origin.label, 80))) return null;
  if (raw.withheldLine !== undefined && !str(raw.withheldLine, MAX.text)) return null;
  if (raw.readLines !== undefined && !strArr(raw.readLines, 8, MAX.text)) return null;
  if (raw.checksNote !== undefined && !str(raw.checksNote, MAX.text)) return null;
  if (!raw.checks.every((c) => (c as Obj).state === undefined || (['passed', 'did not pass', 'did not run', 'not applicable'] as unknown[]).includes((c as Obj).state))) return null;
  if (raw.limitsFull !== undefined && !(isObj(raw.limitsFull) && strArr(raw.limitsFull.read, 40, MAX.text) && strArr(raw.limitsFull.shownNotUsed, 40, MAX.text) && strArr(raw.limitsFull.notRead, 60, MAX.text))) return null;
  if (raw.produced !== undefined && !okProduced(raw.produced)) return null;
  if (raw.display !== undefined && !okDisplay(raw.display)) return null;
  if (raw.blocks !== undefined && !(Array.isArray(raw.blocks) && raw.blocks.length <= MAX.blocks && raw.blocks.every(okBlock))) return null;
  if (raw.sources !== undefined && !okSources(raw.sources)) return null;
  if (raw.critical !== undefined && !(isObj(raw.critical) && Array.isArray(raw.critical.rows) && raw.critical.rows.length <= MAX.rows && raw.critical.rows.every(okLine) && okSources(raw.critical.sources))) return null;
  if (raw.series !== undefined && !(Array.isArray(raw.series) && raw.series.length <= MAX.series && raw.series.every(okSeries))) return null;
  // An answer with no body, or a withheld card that carries a body (A4: a withheld payload carries no text), is malformed.
  if (raw.state === 'answered' && (!Array.isArray(raw.blocks) || !isObj(raw.sources))) return null;
  if (raw.state !== 'answered' && (raw.blocks !== undefined || raw.series !== undefined)) return null;
  return raw as unknown as ExplanationV1;
}

// ── Reading the payload into the card ────────────────────────────────────────────────────────────────────────
/** D-45 dates ("4 Jun 2026"): the engine's one formatter, mirrored. Anything that is not an ISO date stays as sent. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function d45(iso: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m && +m[2] >= 1 && +m[2] <= 12 ? `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}` : (iso ?? '');
}

/** T8: "laboratory not stated" when the record names none. */
export const labOf = (lab: string | undefined) => (!lab || /^Laboratory not stated\b/.test(lab) ? WORDS.labNotStated : lab);

/** The status a result carries (T11); a legacy preliminary flag reads as preliminary. */
export const statusOf = (s: Pick<Source, 'status' | 'preliminary'>): ResultStatus => s.status ?? (s.preliminary ? 'preliminary' : 'final');

/** The engine's count-line forms (explain.ts COUNT), so a count line is drawn as one, verbatim. */
const COUNT_LINE =
  /^\d+ results? · \d+ flagged( \((\d+ critical(, \d+ not classified)?|\d+ not classified)\))?( · \d+ qualitative codes?)?( · \d+ flag unavailable)?$|^\d+ more results? carr(y|ies) a critical laboratory flag; see the results list\.$|^\d+ results? on file could not be read; see the results list\.$|^\d+ problem-list entr(y has|ies have) no recorded status; see the problem list\.$/;
const OUTSIDE_SENTENCE = /; compared by Scrips, the value lies outside the printed reference range /;
const MOST_RECENT = /^Most recent on file, /;

export type FlagView =
  | { kind: 'critical'; text: string; aria: string }
  | { kind: 'code'; text: string; aria: string }
  | { kind: 'blank'; aria: string }
  | { kind: 'plain'; text: string; aria: string };

/** The printed or reported flag, verbatim: repeated reported codes as a comma list (item 5), else the printed field. */
function flagText(s: Source): string {
  const codes = (s.flagCode ?? '').split('~').map((c) => c.trim()).filter(Boolean);
  if (codes.length) return codes.join(', ');
  return (s.printedFlag ?? '').trim();
}
const ABNORMAL_ARIA: Record<string, string> = { H: 'high', L: 'low' };
const CRITICAL_ARIA: Record<string, string> = { HH: 'critical high', LL: 'critical low', AA: 'critical abnormal' };

/** T1 / T2: the Lab flag cell. The code is shown verbatim; a word is added only to the accessible name. */
export function flagView(s: Source): FlagView {
  const cls = s.flagClass ?? 'blank';
  const text = flagText(s);
  switch (cls) {
    case 'critical': {
      const lead = text.split(', ').find((c) => CRITICAL_ARIA[c]);
      return { kind: 'critical', text, aria: lead ? CRITICAL_ARIA[lead] : `${text}, critical` };
    }
    case 'abnormal':
      // N-01 follow-up: H and L carry their meaning in the accessible name; any other code is read as printed.
      return { kind: 'code', text, aria: ABNORMAL_ARIA[text] ?? text };
    case 'qualitative':
      return { kind: 'code', text, aria: text };
    case 'not-classified':
      return { kind: 'plain', text, aria: `Laboratory flag: ${text}` };
    case 'normal-code':
      return { kind: 'plain', text: 'N', aria: 'laboratory code N' };
    case 'unavailable':
      return { kind: 'plain', text: WORDS.flagUnavailable, aria: 'flag unavailable' };
    case 'blank':
    default:
      return { kind: 'blank', aria: WORDS.noFlagAria };
  }
}

export type RangeView = { kind: 'plain'; text: string } | { kind: 'report'; text: string | null } | { kind: 'none' };

/**
 * T3: the Ref range cell. The engine's own parser decides "plain" and says so in the row's sentence ("reference range
 * <verbatim>", or the R2-bis "printed reference range <verbatim>"); the card reads that decision back instead of
 * re-parsing, so the two can never disagree. Anything else is "See report".
 */
export function rangeView(s: Source, sentence: string): RangeView {
  const iv = (s.printedInterval ?? '').trim();
  if (/; no reference range came with this result\.$/.test(sentence)) return { kind: 'none' };
  if (iv && (sentence.endsWith(`; reference range ${iv}.`) || sentence.endsWith(`printed reference range ${iv}.`))) return { kind: 'plain', text: iv };
  return { kind: 'report', text: iv || null };
}

export interface ResultRowView {
  type: 'result';
  /** The engine sentence, verbatim: the row's accessible name, and what opens on tap. */
  sentence: string;
  source: Source;
  /** "Most recent on file · <laboratory>" rows sit under the row they follow (T8). */
  mostRecent: boolean;
  /** R2-bis / T7. */
  outside: boolean;
  flag: FlagView;
  range: RangeView;
  date: string;
  statusTag: string;
  statusAria: string;
  /** T8 / N-01 + encounter owner ruling 1 Nov ("duplicate newest rows on Explain"): the newest result, drawn ONCE as
   *  an indented sub-row under this older flagged row. Its accessible name is the engine's "Most recent on file" sentence
   *  verbatim; every cell is built from the cited source fields. Never on the newest row itself. */
  subRow: ResultRowView | null;
  /** The one series this result is a point of, when it has two or more plotted points (D, L8). */
  trend: TrendView | null;
}
export interface ProblemRowView {
  type: 'problem';
  sentence: string;
  source: Source;
}
export interface TextLineView {
  type: 'line';
  text: string;
}
export type RowView = ResultRowView | ProblemRowView | TextLineView;

export interface TrendView {
  /** The series this trend draws; the card shows one trend per series, on its most recent row. */
  seriesId: string;
  /** Plotted points: x is the collection date in days (calendar-spaced, L4), y the printed value. */
  points: Array<{ day: number; value: number }>;
  label: string;
  /** L8: every observation of the series, plotted or not, oldest first, as a line the card lists. */
  observations: string[];
  /** The engine's rangeNote, verbatim, only when the series' printedInterval is "" (the range changed over time). The
   *  trend never draws a reference band (L4), so such a series needs no band to withhold; the note is drawn under it. */
  rangeNote: string | null;
}

const dayOf = (iso: string) => Math.floor(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / 86_400_000);
/** L4: the ruled label for a point whose only date is the report date. */
export const REPORT_DATE_ONLY = 'Report date; collection date unknown';

/** A plain printed decimal; a qualified ("<0.5"), ratio or text value is never plotted (L6). */
const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;
function trendFor(recordId: string, series: SeriesV1[] | undefined): TrendView | null {
  const s = (series ?? []).find((x) => x.points.some((p) => p.recordId === recordId));
  // L4/L8: only collection-date points of one series, never joined across series; a method-dependent assay is points only.
  if (!s || !s.trendable) return null;
  const pts = s.points.filter((p) => p.dateBasis === 'specimen' && PLAIN_NUMBER.test(p.value.trim()));
  if (pts.length < 2) return null;
  const lab = labOf(s.lab);
  return {
    seriesId: s.id,
    points: pts.map((p) => ({ day: dayOf(p.date), value: Number(p.value) })),
    label: `${s.test}, ${lab}, ${pts.length} results, ${d45(pts[0].date)} to ${d45(pts[pts.length - 1].date)}`,
    observations: s.points.map((p) => {
      const tag = STATUS_TAG[statusOf(p)];
      return [d45(p.date), `${p.value}${p.unit ? ` ${p.unit}` : ''}`, tag, p.dateBasis === 'specimen' ? '' : REPORT_DATE_ONLY].filter(Boolean).join(' · ');
    }),
    rangeNote: s.printedInterval === '' && typeof s.rangeNote === 'string' && s.rangeNote !== '' ? s.rangeNote : null,
  };
}

function resultRow(line: Line, s: Source, series: SeriesV1[] | undefined): ResultRowView {
  const status = statusOf(s);
  return {
    type: 'result',
    sentence: line.text,
    source: s,
    mostRecent: MOST_RECENT.test(line.text),
    subRow: null,
    outside: OUTSIDE_SENTENCE.test(line.text),
    flag: flagView(s),
    range: rangeView(s, line.text),
    date: d45(s.measuredOn),
    statusTag: STATUS_TAG[status],
    statusAria: STATUS_TAG_ARIA[status],
    trend: trendFor(s.recordId, series),
  };
}

/** One fact row read into what the card draws; a line the card cannot bind to one source is drawn as text. */
export function rowOf(line: Line, sources: Record<string, Source>, series?: SeriesV1[]): RowView {
  const s = line.cites.length === 1 ? sources[line.cites[0]] : undefined;
  if (!s || COUNT_LINE.test(line.text)) return { type: 'line', text: line.text };
  if (s.kind === 'problem') return { type: 'problem', sentence: line.text, source: s };
  if ((s.kind === 'lab' || s.kind === 'study') && s.value !== undefined) return resultRow(line, s, series);
  return { type: 'line', text: line.text };
}

export interface AnswerView {
  title: string;
  headline: string | null;
  countLines: string[];
  results: ResultRowView[];
  /** Lines among the results that are not a row (a tie, mixed date bases, an R2-bis overflow count). */
  resultNotes: string[];
  problems: ProblemRowView[];
  gaps: string[];
  /** The engine's limits lines, verbatim: the at-rest line first, then any dated-results or omitted-items line. */
  limitNotes: string[];
}


/** An answered payload, read into the card's parts, in the payload's own order. */
export function answerView(x: ExplanationV1): AnswerView {
  const sources = x.sources ?? {};
  const view: AnswerView = { title: x.title ?? '', headline: null, countLines: [], results: [], resultNotes: [], problems: [], gaps: [], limitNotes: [] };
  for (const b of x.blocks ?? []) {
    if (b.kind === 'headline') view.headline = b.text;
    else if (b.kind === 'gaps') view.gaps.push(...b.items.map((i) => i.text));
    else if (b.kind === 'limits') view.limitNotes.push(...b.items);
    else
      for (const line of b.rows) {
        const r = rowOf(line, sources, x.series);
        const prev = view.results[view.results.length - 1];
        // T8 / N-01: the engine's "Most recent on file" line is a sub-row of the older FLAGGED row it follows, never a
        // row of its own. With no older flagged row before it, it stays an ordinary row.
        if (r.type === 'result' && r.mostRecent && prev && !prev.subRow && isFlagged(prev)) prev.subRow = r;
        else if (r.type === 'result') view.results.push(r);
        else if (r.type === 'problem') view.problems.push(r);
        else if (COUNT_LINE.test(r.text)) view.countLines.push(r.text);
        else view.resultNotes.push(r.text);
      }
  }
  // Ruling 1 Nov ("duplicate newest rows on Explain"): a result drawn as a sub-row is never also a standalone row. The
  // dropped row's own engine sentence still decides the R2-bis marker (the engine compared that same record item).
  const subIds = new Set(view.results.flatMap((r) => (r.subRow ? [r.subRow.source.recordId] : [])));
  for (const r of view.results) {
    const dup = r.subRow ? view.results.find((d) => d !== r && d.source.recordId === r.subRow!.source.recordId) : undefined;
    if (r.subRow && dup?.outside) r.subRow = { ...r.subRow, outside: true };
  }
  view.results = view.results.filter((r) => !subIds.has(r.source.recordId) || !!r.subRow);
  // One trend per series (craft review): only the series' most recent listed row (or sub-row) carries it.
  const latest = new Map<string, ResultRowView>();
  for (const r of view.results.flatMap((row) => (row.subRow ? [row, row.subRow] : [row]))) {
    if (!r.trend) continue;
    const cur = latest.get(r.trend.seriesId);
    if (!cur || (r.source.measuredOn ?? '') > (cur.source.measuredOn ?? '')) latest.set(r.trend.seriesId, r);
  }
  const keep = (r: ResultRowView): ResultRowView => (r.trend && latest.get(r.trend.seriesId) !== r ? { ...r, trend: null } : r);
  view.results = view.results.map((r) => ({ ...keep(r), subRow: r.subRow ? keep(r.subRow) : null }));
  return view;
}

/** A row the laboratory flagged (critical, abnormal, qualitative or a not-classified flag): the only kind of row a
 *  "Most recent on file" sub-row may sit under. */
function isFlagged(r: ResultRowView): boolean {
  const c = r.source.flagClass ?? 'blank';
  return c === 'critical' || c === 'abnormal' || c === 'qualitative' || c === 'not-classified';
}

/** X5: a withheld card's critical rows, each bound to its own source result. */
export function criticalRowsOf(x: ExplanationV1): ResultRowView[] {
  if (!x.critical) return [];
  return x.critical.rows
    .map((line) => rowOf(line, x.critical!.sources))
    .filter((r): r is ResultRowView => r.type === 'result' && r.source.flagClass === 'critical');
}

/** O2: every record item the card cites, for "How this was produced". */
export function inputsOf(x: ExplanationV1): Source[] {
  const all = { ...(x.sources ?? {}), ...(x.critical?.sources ?? {}) };
  return Object.values(all).filter((s) => s.kind !== 'state');
}
